// src/App.js
import { Routes, Route, Navigate } from 'react-router-dom';

import ProtectedRoute from './components/ProtectedRoute';
import RequireRole from './components/RequireRole';
import ErrorBoundary from './components/ErrorBoundary';
import RequireSubrole from './components/RequireSubrole';
import SmartRedirect from './components/SmartRedirect';
import MainLayout from './layouts/MainLayout';
import { useResponsiveSidebar } from './hooks/useResponsiveSidebar';


import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import DashboardInicio from './pages/DashboardInicio';
import Activos from './pages/Activos';
import ActivosForm from './pages/ActivosForm';
import ActivoDetail from './pages/ActivoDetail';
import MisActivos from './pages/MisActivos';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import MisTickets from './pages/MisTickets';
import Aplicaciones from './pages/Aplicaciones';
import AlmuerzosRedireccion from './pages/AlmuerzosRedireccion';
import AplicacionDetalle from './pages/AplicacionDetalle';

import AdminCategoriasTicket from './pages/AdminCategoriasTicket';
import Usuarios from './pages/Usuarios';
import UsuariosForm from './pages/UsuariosForm';
import UsuarioDetail from './pages/UsuarioDetail';
import Reportes from './pages/Reportes';
import ConfiguracionAdmin from './pages/ConfiguracionAdmin';
import Perfil from './pages/Perfil';
import Ajustes from './pages/Ajustes';
import Actas from './pages/Actas';

import GestionActas from './pages/GestionActas';
import ActaDetail from './pages/ActaDetail';
import PrevisualizarActa from './pages/PrevisualizarActa';
import PazYSalvo from './pages/PazYSalvo';
import PazYSalvoJefeDirecto from './pages/PazYSalvoJefeDirecto';
import PazYSalvoTI from './pages/PazYSalvoTI';
import PazYSalvoContabilidad from './pages/PazYSalvoContabilidad';
import PazYSalvoGerenciaFinanzas from './pages/PazYSalvoGerenciaFinanzas';
import PazYSalvoGerenteGeneral from './pages/PazYSalvoGerenteGeneral';
import MicrosoftCallback from './pages/MicrosoftCallback';
import SsoCallback from './pages/SsoCallback';
import ResponderTicket from './pages/ResponderTicket';
import Documentacion from './pages/Documentacion';
import Contacto from './pages/Contacto';
import Privacidad from './pages/Privacidad';
import EvidenciasSeguridad from './pages/EvidenciasSeguridad';
import BaseConocimiento from './pages/BaseConocimiento';
import ArticuloBaseConocimiento from './pages/ArticuloBaseConocimiento';
import EditorArticulo from './pages/EditorArticulo';
import AdminBaseConocimiento from './pages/AdminBaseConocimiento';
import MiEquipo from './pages/MiEquipo';

export default function App() {
  const { isSidebarOpen, toggleSidebar } = useResponsiveSidebar();

  return (
    <ErrorBoundary>
    <Routes>
      {/* Públicas */}
      <Route path="/login" element={<Login />} />
      <Route path="/access-denied" element={<AccessDenied />} />


      <Route path="/contact" element={<Contacto />} />
      <Route path="/privacy" element={<Privacidad />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
      <Route path="/sso-callback" element={<SsoCallback />} />
      <Route path="/responder/:token" element={<ResponderTicket />} />

      {/* Redirección de la ruta raíz según el rol del usuario */}
      <Route path="/" element={<SmartRedirect />} />

      {/* Privadas */}
      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <MainLayout
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={toggleSidebar}
            />
          }
        >
          {/* Dashboard - Admin y soporte ven el de gestión; el resto, el personal */}
          <Route path="/dashboard" element={<DashboardInicio />} />

          {/* Activos - Solo admin y soporte */}
          <Route
            path="activos"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <Activos />
              </RequireRole>
            }
          />
          <Route path="activos/nuevo" element={<Navigate to="/activos?nuevo=1" replace />} />
          <Route
            path="activos/:id"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <ActivoDetail />
              </RequireRole>
            }
          />
          <Route
            path="activos/:id/editar"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <ActivosForm edit={true} />
              </RequireRole>
            }
          />

          {/* Mis Activos - Todos los usuarios */}
          <Route path="mis-activos" element={<MisActivos />} />

          {/* Usuarios - Solo admin y soporte */}
          <Route
            path="usuarios"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <Usuarios />
              </RequireRole>
            }
          />
          <Route path="usuarios/nuevo" element={<Navigate to="/usuarios?nuevo=1" replace />} />
          <Route
            path="usuarios/:id/editar"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <UsuariosForm edit={true} />
              </RequireRole>
            }
          />
          <Route
            path="usuarios/:id"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <UsuarioDetail />
              </RequireRole>
            }
          />

          {/* Documentación - Admin, soporte, SAP */}
          <Route
            path="/docs"
            element={
              <RequireRole roles={['admin', 'soporte', 'sap']}>
                <Documentacion />
              </RequireRole>
            }
          />

          {/* Tickets - Admin, soporte y SAP */}
          <Route
            path="tickets"
            element={
              <RequireRole roles={['admin', 'soporte', 'sap']}>
                <Tickets />
              </RequireRole>
            }
          />
          <Route path="tickets/:id" element={<TicketDetail />} />



          {/* Admin Categorías Ticket - Solo admin */}
          {['admin/categorias-ticket', 'admin-categorias-ticket'].map((p) => (
            <Route
              key={p}
              path={p}
              element={
                <RequireRole roles={['admin']}>
                  <AdminCategoriasTicket />
                </RequireRole>
              }
            />
          ))}

          {/* Almuerzos ya no vive aquí: los enlaces y marcadores viejos llevan a la app nueva */}
          <Route path="almuerzos" element={<AlmuerzosRedireccion />} />

          {/* Aplicaciones del holding - Solo admin */}
          <Route path="aplicaciones" element={<RequireRole roles={['admin']}><Aplicaciones /></RequireRole>} />
          <Route path="aplicaciones/:codigo" element={<RequireRole roles={['admin']}><AplicacionDetalle /></RequireRole>} />

          {/* Mis Tickets - Todos los usuarios */}
          <Route path="mis-tickets" element={<MisTickets />} />

          {/* Actas - Todos los usuarios */}
          <Route path="actas" element={<Actas />} />
          <Route path="actas/:id" element={<ActaDetail />} />
          <Route path="actas/previsualizar-firmado/:actaId" element={<PrevisualizarActa />} />

          {/* Mi Equipo - Todos los usuarios autenticados */}
          <Route path="mi-equipo" element={<MiEquipo />} />



          {/* Gestión de Actas - Solo admin y soporte */}
          <Route
            path="gestion-actas"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <GestionActas />
              </RequireRole>
            }
          />
          <Route
            path="gestion-actas/:id"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <ActaDetail />
              </RequireRole>
            }
          />

          {/* Paz y Salvo - Solo RRHH (gestión completa) */}
          <Route
            path="pazysalvo"
            element={
              <RequireSubrole subroles={['RRHH']}>
                <PazYSalvo />
              </RequireSubrole>
            }
          />

          {/* Paz y Salvo - Admin (gestión completa) */}
          <Route
            path="pazysalvo-admin"
            element={
              <RequireRole roles={['admin', 'rrhh']}>
                <PazYSalvo />
              </RequireRole>
            }
          />

          {/* Paz y Salvo - Subroles específicos */}
          <Route
            path="pazysalvo/jefe-directo"
            element={
              <RequireSubrole subroles={['JefeInmediato']}>
                <PazYSalvoJefeDirecto />
              </RequireSubrole>
            }
          />
          <Route
            path="pazysalvo/ti"
            element={
              <RequireSubrole subroles={['Informatica', 'TI']}>
                <PazYSalvoTI />
              </RequireSubrole>
            }
          />
          <Route
            path="pazysalvo/contabilidad"
            element={
              <RequireSubrole subroles={['Contabilidad']}>
                <PazYSalvoContabilidad />
              </RequireSubrole>
            }
          />
          <Route
            path="pazysalvo/gerencia-finanzas"
            element={
              <RequireSubrole subroles={['GerenciaFinanzas']}>
                <PazYSalvoGerenciaFinanzas />
              </RequireSubrole>
            }
          />
          <Route
            path="pazysalvo/gerente-general"
            element={
              <RequireSubrole subroles={['GerenteGeneral']}>
                <PazYSalvoGerenteGeneral />
              </RequireSubrole>
            }
          />


          {/* Páginas de usuario */}
          <Route path="perfil" element={<Perfil />} />
          <Route path="ajustes" element={<Ajustes />} />

          {/* Solo ADMIN */}
          <Route
            path="reportes"
            element={
              <RequireRole roles={['admin']}>
                <Reportes />
              </RequireRole>
            }
          />

          {/* Evidencias de Seguridad - Admin y Soporte */}
          <Route
            path="evidencias-seguridad"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <EvidenciasSeguridad />
              </RequireRole>
            }
          />

          {/* Base de Conocimiento - Admin y Soporte */}
          <Route path="base-conocimiento" element={<BaseConocimiento />} />
          <Route path="base-conocimiento/nuevo" element={<RequireRole roles={['admin', 'soporte']}><EditorArticulo /></RequireRole>} />
          <Route path="base-conocimiento/editar/:id" element={<RequireRole roles={['admin', 'soporte']}><EditorArticulo /></RequireRole>} />
          <Route path="base-conocimiento/articulo/:id" element={<ArticuloBaseConocimiento />} />

          {/* Administrar Base de Conocimiento - Solo Admin y Soporte */}
          <Route
            path="admin-base-conocimiento"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <AdminBaseConocimiento />
              </RequireRole>
            }
          />

          <Route
            path="configuracion"
            element={
              <RequireRole roles={['admin', 'soporte']}>
                <ConfiguracionAdmin />
              </RequireRole>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<SmartRedirect />} />
        </Route>
      </Route>
    </Routes>
    </ErrorBoundary>
  );
}
